// Nine Lives '84 - object views.
//
// A view tracks one simulation body: its model, its previous and current
// poses, and per-frame presentation (interpolation, facing, run bob,
// tumbles). Call `commit()` after every tick and `present(alpha, time)`
// per frame. Mice and junk come and go, so they are pooled by identity.

import * as THREE from 'three';
import type { Alley, Junk, Mouse } from '../sim/alley';
import type { Cat } from '../sim/cat';
import type { Box } from '../sim/types';
import { lerpPose, type Pose } from './interpolate';
import { shadowify } from './materials';
import {
  buildCat,
  buildDog,
  buildFelicia,
  buildGarment,
  buildJunk,
  buildMouse,
  buildWindow,
  randomLaundryColour,
  WALL_Z,
  type CatModel,
  type DogModel,
  type WindowModel,
} from './models';

/** Laundry on one clothesline: garments at base offsets that ride the line. */
interface LaundryLine {
  x0: number;
  span: number;
  y: number;
  drift: number;
  offset: number;
  garments: { obj: THREE.Object3D; base: number; sway: number }[];
}

export class EntityView {
  readonly prev: Pose = { x: 0, y: 0, facing: 1 };
  readonly cur: Pose = { x: 0, y: 0, facing: 1 };
  readonly tmp: Pose = { x: 0, y: 0, facing: 1 };

  constructor(readonly root: THREE.Object3D) {}

  commit(b: Box, facing = 1): void {
    this.prev.x = this.cur.x;
    this.prev.y = this.cur.y;
    this.prev.facing = this.cur.facing;
    this.cur.x = b.x;
    this.cur.y = b.y;
    this.cur.facing = facing;
  }

  snap(): void {
    this.prev.x = this.cur.x;
    this.prev.y = this.cur.y;
    this.prev.facing = this.cur.facing;
  }

  pose(alpha: number): Pose {
    return lerpPose(this.prev, this.cur, alpha, this.tmp);
  }
}

export class CatView {
  readonly model: CatModel;
  private readonly view: EntityView;
  private speed = 0;
  private airborne = false;
  private leaping = false;
  private caught = false;
  private knocked = false;
  private invuln = false;
  /** Set by the world: paddling pose, and the pink of a cat short of air. */
  swimming = false;
  gasping = false;
  private tinted = false;

  constructor(scene: THREE.Scene, fur?: number, light?: number) {
    this.model = buildCat(fur, light);
    this.view = new EntityView(this.model.root);
    scene.add(this.model.root);
  }

  get root(): THREE.Group {
    return this.model.root;
  }

  /** Copy the latest simulation state (once per tick). */
  commit(cat: Cat): void {
    this.view.commit(cat, cat.facing);
    this.speed = Math.abs(cat.vx);
    this.airborne = !cat.onGround;
    this.leaping = cat.leaping;
    this.caught = cat.state === 'caught';
    this.knocked = cat.knockedTicks > 0;
    this.invuln = cat.invuln > 0;
  }

  /** Jump straight to the current state (new game, scene change). */
  snap(): void {
    this.view.snap();
  }

  present(alpha: number, time: number): void {
    const p = this.view.pose(alpha);
    const { root, body, legs, tail } = this.model;
    root.position.set(p.x, p.y, 0);
    body.scale.x = p.facing;
    root.visible = !this.invuln || Math.floor(time * 12) % 2 === 0;

    if (this.gasping !== this.tinted) {
      this.tinted = this.gasping;
      for (const m of this.model.fur) m.material = this.gasping ? PINK : this.model.furMaterial;
    }

    // Legs scissor while running; tuck while airborne; paddle in water.
    const stride = this.speed > 5 ? Math.sin(time * 18) * 0.6 : 0;
    legs.forEach((leg, i) => {
      leg.rotation.z = this.swimming
        ? Math.sin(time * 10 + i * 1.5) * 0.7
        : this.airborne
          ? this.leaping
            ? 0.8
            : 0.3
          : stride * (i % 2 ? 1 : -1);
    });
    if (this.swimming && !this.caught) {
      body.rotation.z = Math.sin(time * 2) * 0.1 - 0.1;
      body.position.y = Math.sin(time * 3) * 0.6;
    } else if (this.caught) {
      body.rotation.z = Math.sin(time * 40) * 0.6;
      body.position.y = Math.abs(Math.sin(time * 25)) * 3;
    } else if (this.knocked) {
      body.rotation.z = Math.sin(time * 30) * 0.5;
      body.position.y = 0;
    } else {
      body.rotation.z = this.leaping ? 0.15 : 0;
      body.position.y = 0;
    }
    tail.rotation.z = -0.5 - Math.sin(time * 4) * 0.25;
  }
}

/** Everything in the alley except Tabs. */
export class AlleyViews {
  private readonly group = new THREE.Group();
  private readonly windows: WindowModel[] = [];
  private readonly dog: DogModel;
  private readonly dogView: EntityView;
  private dogSpeed = 0;
  private dogBiting = false;
  private readonly rival: CatModel;
  private readonly rivalView: EntityView;
  private rivalAngry = false;
  private readonly mice = new Map<Mouse, EntityView>();
  private readonly junk = new Map<Junk, EntityView>();
  private readonly felicia: CatModel;
  private readonly laundry: LaundryLine[] = [];

  constructor(
    private readonly scene: THREE.Object3D,
    alley: Alley,
  ) {
    this.felicia = buildFelicia();
    this.felicia.root.visible = false;
    this.group.add(this.felicia.root);
    // A random wash on each line: four to six garments, jittered along it.
    for (const l of alley.lines) {
      const count = 4 + Math.floor(Math.random() * 3);
      const line: LaundryLine = {
        x0: l.x,
        span: l.w,
        y: l.y + l.h,
        drift: 0,
        offset: Math.random() * l.w,
        garments: [],
      };
      for (let i = 0; i < count; i++) {
        const obj = buildGarment(Math.floor(Math.random() * 4), randomLaundryColour());
        shadowify(obj);
        const base = (i * l.w) / count + (Math.random() - 0.5) * 16;
        line.garments.push({ obj, base, sway: Math.random() * 6 });
        this.group.add(obj);
      }
      this.laundry.push(line);
    }
    for (const w of alley.windows) {
      const m = buildWindow(w);
      this.windows.push(m);
      this.group.add(m.root);
    }
    this.dog = buildDog();
    this.dogView = new EntityView(this.dog.root);
    this.rival = buildCat(0xd08a3a, 0xf0c090);
    this.rivalView = new EntityView(this.rival.root);
    this.group.add(this.dog.root, this.rival.root);
    scene.add(this.group);
    this.commit(alley);
    this.dogView.snap();
    this.rivalView.snap();
  }

  dispose(): void {
    this.scene.remove(this.group);
  }

  commit(alley: Alley): void {
    alley.windows.forEach((w, i) => {
      this.windows[i].interior.material = w.felicia ? SHARED_PINK : w.open ? SHARED_LIT : SHARED_DARK;
      this.windows[i].openness = w.open ? 1 : 0; // eased in present()
      if (w.felicia) {
        this.felicia.root.position.set(w.x + w.w / 2 - 7, w.y + 1, WALL_Z + 5);
        this.felicia.root.visible = true;
      }
    });
    if (!alley.feliciaWindow) this.felicia.root.visible = false;
    alley.lines.forEach((l, i) => {
      this.laundry[i].drift = alley.drift(l);
    });
    const d = alley.dog;
    this.dogView.commit(d, d.dir);
    this.dogSpeed = d.away > 0 || d.biting ? 0 : 1;
    this.dogBiting = d.biting;
    this.rivalView.commit(alley.rival, alley.rival.x < 160 ? 1 : -1);
    this.rivalAngry = alley.rival.linger > 0;
    this.pool(this.mice, alley.mice, buildMouse, (m) => m.dir);
    this.pool(this.junk, alley.junk, buildJunk, () => 1);
  }

  private pool<T extends Box>(
    views: Map<T, EntityView>,
    live: T[],
    build: () => THREE.Object3D,
    facing: (t: T) => number,
  ): void {
    const seen = new Set<T>();
    for (const e of live) {
      seen.add(e);
      let v = views.get(e);
      if (!v) {
        v = new EntityView(build());
        shadowify(v.root);
        v.commit(e, facing(e));
        v.snap();
        views.set(e, v);
        this.group.add(v.root);
      } else {
        v.commit(e, facing(e));
      }
    }
    for (const [e, v] of views) {
      if (!seen.has(e)) {
        this.group.remove(v.root);
        views.delete(e);
      }
    }
  }

  present(alpha: number, time: number, dt: number): void {
    for (const w of this.windows) {
      const target = w.openness;
      const cur = (w.sash.userData.open as number | undefined) ?? 0;
      const next = cur + (target - cur) * Math.min(1, dt * 6);
      w.sash.userData.open = next;
      w.sash.position.y = w.closedY + next * w.travel;
    }

    const dp = this.dogView.pose(alpha);
    this.dog.root.position.set(dp.x, dp.y, 0);
    this.dog.body.scale.x = dp.facing;
    const stride = this.dogSpeed ? Math.sin(time * 16) * 0.7 : 0;
    this.dog.legs.forEach((leg, i) => {
      leg.rotation.z = stride * (i % 2 ? 1 : -1);
    });
    this.dog.jaw.position.y = this.dogBiting ? 4.6 + Math.abs(Math.sin(time * 30)) * 1.5 : 5.6;

    this.felicia.tail.rotation.z = -0.6 - Math.sin(time * 2.5) * 0.3;

    // Laundry rides its line and wraps round the far pulley; a little sway.
    for (const l of this.laundry) {
      l.offset = (((l.offset + l.drift * dt) % l.span) + l.span) % l.span;
      for (const g of l.garments) {
        const x = l.x0 + ((((g.base + l.offset) % l.span) + l.span) % l.span);
        g.obj.position.set(x, l.y, 0);
        g.obj.rotation.z = Math.sin(time * 1.8 + g.sway) * 0.07 - l.drift * 0.004;
      }
    }

    const rp = this.rivalView.pose(alpha);
    this.rival.root.position.set(rp.x, rp.y, 0);
    this.rival.body.scale.x = rp.facing;
    this.rival.tail.rotation.z = this.rivalAngry
      ? -0.9 - Math.sin(time * 20) * 0.5
      : -0.4 - Math.sin(time * 3) * 0.2;

    for (const v of this.mice.values()) {
      const p = v.pose(alpha);
      v.root.position.set(p.x, p.y, 0);
      v.root.scale.x = p.facing;
    }
    for (const v of this.junk.values()) {
      const p = v.pose(alpha);
      v.root.position.set(p.x, p.y, 0);
      v.root.rotation.z = time * 6;
    }
  }
}

const PINK = new THREE.MeshToonMaterial({ color: 0xe86aa8 });
const SHARED_DARK = new THREE.MeshToonMaterial({ color: 0x1a2230 });
const SHARED_LIT = new THREE.MeshBasicMaterial({ color: 0xffe9a8, toneMapped: false });
const SHARED_PINK = new THREE.MeshBasicMaterial({ color: 0xffb8d0, toneMapped: false });
